import { useToggle } from 'ahooks'
import { Modal } from 'antd'
import moment from 'moment'
import React from 'react'
import { from } from 'rxjs'
import { post } from 'rxjs-file-upload'
import { concatMap, map } from 'rxjs/operators'

import { useIsFeatureToggleOn } from '@/components/feature-toggle/feature-toggle.hooks'
import {
  AnniversaryCardInfoFragment,
  LocalFileFragment,
  PostType,
  useFileCreateMutation,
  useGetUserAnniversaryCardsQuery,
  useGetViewerQuery,
} from '@/generated/graphql'

import { loadFont } from '../annual-report/annual-report.util'
import { PostEditor } from '../post/post-editor'
import { AnniversaryCardModalPage } from './anniversary-card-modal-page.component'
import { getIsAtAnniversaryDateRange } from './anniversary-card.util'
import { drawAnniversaryCard, getAnniversaryBackgroundImageUrl } from './draw-anniversary-card-util'

export const AnniversaryCardContainer: React.FC<{
  isViewMode?: boolean
  setViewMode?: React.Dispatch<React.SetStateAction<boolean>>
  anniversary?: string
}> = ({ isViewMode = false, setViewMode, anniversary }) => {
  const canvasWidth = 640
  const canvasHeight = 1007
  const maxHeight = (window.innerHeight - 10) * 0.9
  const maxWidth = window.innerWidth * 0.9
  const isCanvasHeightHigher = canvasHeight > maxHeight
  const isCanvasWidthHigher = canvasWidth > maxWidth
  const scaling =
    isCanvasHeightHigher || isCanvasWidthHigher
      ? maxHeight / canvasHeight > maxWidth / canvasWidth
        ? maxWidth / canvasWidth
        : maxHeight / canvasHeight
      : 1
  const imageHeight = canvasHeight * scaling
  const imageWidth = canvasWidth * scaling

  const [anniversaryCardVisible, { toggle: anniversaryCardToggle }] = setViewMode
    ? [isViewMode, { toggle: setViewMode }]
    : useToggle(false)
  const [postEditorPreReadyVisible, { toggle: postEditorPreReadyToggle }] = useToggle(false)
  const [postEditorReadyVisible, { toggle: postEditorReadyToggle }] = useToggle(false)
  const [canvasList, setCanvasList] = React.useState<HTMLCanvasElement[]>([])
  const [images, setImages] = React.useState<File[]>([])
  const [files, setFiles] = React.useState<LocalFileFragment[]>([])
  const [font, setFont] = React.useState<FontFace>()

  const isFeatureToggleOn = useIsFeatureToggleOn('anniversaryCard')

  const { data: viewerData } = useGetViewerQuery({
    variables: {
      withHireDate: true,
    },
  })

  const { data } = useGetUserAnniversaryCardsQuery({
    skip: !isFeatureToggleOn || !viewerData || !viewerData.viewer.userId,
    variables: {
      userId: viewerData?.viewer.userId || 0,
      anniversary: anniversary || viewerData?.viewer.latestAnniversary || moment().year().toString(),
    },
  })

  const [upload] = useFileCreateMutation()

  const cardReceiver = data?.user
  const hireDate = cardReceiver?.hireDate
  const anniversaryCards = cardReceiver?.anniversaryCards || []
  const anniversaryYears = hireDate
    ? (viewerData?.viewer.latestAnniversary ? Number(viewerData?.viewer.latestAnniversary) : moment().year()) -
      moment(hireDate).year()
    : 1

  const createImage = ({
    image,
    anniversaryCard,
    anniversaryYears,
  }: {
    image: HTMLImageElement
    anniversaryCard: AnniversaryCardInfoFragment
    anniversaryYears: number
  }) => {
    const canvas = document.createElement('canvas')
    canvas.width = canvasWidth
    canvas.height = canvasHeight
    const ctx = canvas.getContext('2d')
    drawAnniversaryCard(ctx!, image, anniversaryCard, anniversaryYears)
    return canvas
  }

  React.useEffect(() => {
    setFont(loadFont('ACFont'))
  }, [])

  React.useEffect(() => {
    if (postEditorPreReadyVisible) {
      const image = new Image()
      image.onload = () => {
        const canvasList = anniversaryCards.map((anniversaryCard) =>
          createImage({ image, anniversaryCard, anniversaryYears })
        )
        setCanvasList(canvasList)
      }
      image.crossOrigin = 'anonymous'
      image.src = getAnniversaryBackgroundImageUrl(anniversaryYears)
    }
  }, [postEditorPreReadyVisible])

  React.useEffect(() => {
    if (canvasList.length > 0) {
      canvasList.forEach((canvas, index) =>
        canvas.toBlob((blob: any) => {
          const image = new File([blob], `anniversaryCard_${index}.jpg`, { type: 'image/jpeg' })
          setImages((images) => [...images, image])
        })
      )
    }
  }, [canvasList])

  React.useEffect(() => {
    // 检查 images 是否 ready，images = 已填写周年贺卡 + default 卡片
    if (images.length > 0 && images.length === anniversaryCards.length) {
      from(images)
        .pipe(
          concatMap((file) =>
            from(
              upload({
                variables: {
                  file: {
                    name: file.name,
                    size: file.size,
                    type: file.type,
                  },
                },
              })
            ).pipe(
              concatMap(({ data }) => {
                const fileData = data!.fileCreate
                return post({
                  url: fileData.oss.uploadUrl,
                  body: (() => {
                    const formData = Object.keys(fileData.oss).reduce(
                      (formData, key) => (formData.append(key, fileData.oss[key]), formData),
                      new FormData()
                    )
                    formData.append('file', file)
                    return formData
                  })(),
                }).pipe(map(() => fileData))
              })
            )
          )
        )
        .subscribe((file) => setFiles((files) => [...files, file]))
    }
  }, [images])

  React.useEffect(() => {
    if (files.length > 0 && files.length === anniversaryCards.length) {
      postEditorReadyToggle(true)
    }
  }, [files])

  React.useEffect(() => {
    if (
      !setViewMode &&
      cardReceiver &&
      cardReceiver.anniversaryCards &&
      cardReceiver.anniversaryCards.every((anniversaryCard) => !anniversaryCard.isRead) &&
      getIsAtAnniversaryDateRange(hireDate)
    ) {
      font?.loaded.then(() => {
        anniversaryCardToggle(true)
      })
    }
  }, [cardReceiver, setViewMode])

  if (!isFeatureToggleOn) {
    return null
  }

  return (
    <>
      <Modal
        visible={anniversaryCardVisible}
        footer={null}
        width={imageWidth}
        onCancel={() => anniversaryCardToggle(false)}
        style={{ top: 10 }}
        bodyStyle={{ padding: 0 }}
        destroyOnClose
        closable={false}
        maskClosable={false}
      >
        <AnniversaryCardModalPage
          onPostEditorModalOpen={() => postEditorPreReadyToggle(true)}
          onAnniversaryCardModalClose={() => anniversaryCardToggle(false)}
          anniversaryCards={anniversaryCards}
          postEditorPreReadyVisible={postEditorPreReadyVisible}
          postEditorReadyVisible={postEditorReadyVisible}
          backgroundUrl={getAnniversaryBackgroundImageUrl(anniversaryYears)}
          anniversaryYears={anniversaryYears}
          isViewMode={isViewMode}
          canvasHeight={canvasHeight}
          canvasWidth={canvasWidth}
          imageHeight={imageHeight}
          imageWidth={imageWidth}
        />
      </Modal>
      {!isViewMode && (
        <PostEditor
          groupId={1} // 通用群组
          visible={postEditorReadyVisible}
          onClose={() => postEditorReadyToggle(false)}
          postType={PostType.General}
          preFiles={files}
        />
      )}
    </>
  )
}
